import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { useAuth } from "@/context/auth/auth-context"
import { useCurrency } from "@/context/currency/currency-context"
import { calculateProjection } from "@/lib/projection-math"
import type { AssetProjection, ContributionStep as MathContributionStep } from "@/lib/projection-math"
import hive, { getCashBalance } from "@/lib/api/hive"
import type { FireDashboard, UpdateFireSettingsInput } from "@/types/fire"
import { FireHero } from "@/components/custom/fire/fire-hero"
import { FireProgressBar } from "@/components/custom/fire/fire-progress-bar"
import { FireSettingsPanel } from "@/components/custom/fire/fire-settings-panel"
import { FireProjectionChart } from "@/components/custom/fire/fire-projection-chart"
import { FireScenarioChart } from "@/components/custom/fire/fire-scenario-chart"
import { FireMilestones } from "@/components/custom/fire/fire-milestones"
import { FireStreakBadge } from "@/components/custom/fire/fire-streak-badge"
import { FireIncomeHistory } from "@/components/custom/fire/fire-income-history"
import { FireShareDialog } from "@/components/custom/fire/fire-share-dialog"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { InfoTip } from "@/components/custom/fire/info-tip"
import { TrendingUp, Share2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Analytics } from "@/lib/analytics"

function LoadingSkeleton() {
	return (
		<div className="max-w-6xl mx-auto p-4 space-y-6">
			{/* Header */}
			<Skeleton className="h-8 w-64" />
			{/* Hero: 5 cards in 2/5 grid */}
			<div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
				{[1, 2, 3, 4, 5].map((i) => (
					<Skeleton key={i} className="h-28 rounded-lg" />
				))}
			</div>
			{/* Progress bar */}
			<Skeleton className="h-12 rounded-lg w-full" />
			{/* Streak badge */}
			<Skeleton className="h-10 w-48 rounded-full" />
			{/* Settings panel */}
			<Skeleton className="h-14 rounded-lg w-full" />
			{/* Chart */}
			<div className="bg-white/60 dark:bg-black/20 backdrop-blur-md border border-sky-200/50 dark:border-sky-800/30 rounded-lg p-4">
				<Skeleton className="h-64 rounded-lg" />
			</div>
		</div>
	)
}

export function FirePage() {
	const { token } = useAuth()
	const { convert } = useCurrency()

	const [dashboard, setDashboard] = useState<FireDashboard | null>(() => {
		try {
			const cached = sessionStorage.getItem("beediv-fire-dashboard")
			return cached ? JSON.parse(cached) : null
		} catch {
			return null
		}
	})
	const [loading, setLoading] = useState(() => {
		try {
			return !sessionStorage.getItem("beediv-fire-dashboard")
		} catch {
			return true
		}
	})
	const [error, setError] = useState<string | null>(null)
	const [saving, setSaving] = useState(false)
	const [totalCash, setTotalCash] = useState(0)
	const [settingsPreview, setSettingsPreview] = useState<Partial<UpdateFireSettingsInput>>({})
	const [showScenarios, setShowScenarios] = useState(false)
	const [showShareDialog, setShowShareDialog] = useState(false)

	const updateDashboard = useCallback((data: FireDashboard) => {
		setDashboard(data)
		try {
			sessionStorage.setItem("beediv-fire-dashboard", JSON.stringify(data))
		} catch {
			/* ignore quota errors */
		}
	}, [])

	const fetchCashBalances = useCallback(
		async (portfolios: { id: string }[]) => {
			if (portfolios.length === 0) return
			const results = await Promise.all(
				portfolios.map((p) => getCashBalance(p.id).catch(() => null)),
			)
			const total = results.reduce(
				(sum, bal) => (bal?.enabled ? sum + convert(bal.balance, bal.currency) : sum),
				0,
			)
			setTotalCash(total)
		},
		[convert],
	)

	const fetchDashboard = useCallback(async () => {
		if (!token) return
		try {
			const data = await hive.getFireDashboard(token)
			updateDashboard(data)
			fetchCashBalances(data.portfolios)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load FIRE dashboard")
		} finally {
			setLoading(false)
		}
	}, [token, updateDashboard, fetchCashBalances])

	useEffect(() => {
		fetchDashboard()
	}, [fetchDashboard])

	const refreshDashboard = useCallback(async () => {
		if (!token) return
		const data = await hive.getFireDashboard(token)
		updateDashboard(data)
		fetchCashBalances(data.portfolios)
	}, [token, updateDashboard, fetchCashBalances])

	const handleSaveSettings = async (input: UpdateFireSettingsInput) => {
		if (!token) return
		setSaving(true)
		try {
			await hive.updateFireSettings(input, token)
			setSettingsPreview({})
			await refreshDashboard()
			Analytics.capture("FIRE Settings Saved")
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to save settings")
		} finally {
			setSaving(false)
		}
	}

	// Merge live form edits over saved settings so the projection updates without saving
	const effectiveSettings = dashboard ? { ...dashboard.settings, ...settingsPreview } : null

	// Currency in which effectiveSettings monetary values are expressed
	const settingsCurrency = effectiveSettings?.currency ?? dashboard?.settings.currency ?? "USD"

	// Convert contribution schedule from snake_case (API) to camelCase (projection-math),
	// converting amounts from stored currency to display currency
	const contributionSchedule: MathContributionStep[] | undefined =
		effectiveSettings?.contribution_schedule?.map((s) => ({
			fromYear: s.from_year,
			toYear: s.to_year,
			monthlyAmount: convert(s.monthly_amount, settingsCurrency),
		})) ?? undefined

	// Build projection from dashboard data
	const projection =
		dashboard && effectiveSettings
			? (() => {
					const positionAssets: AssetProjection[] = dashboard.portfolios.flatMap((portfolio) =>
						portfolio.positions
							.filter((p) => p.shares > 0)
							.map((p) => ({
								value: convert(p.price * p.shares, p.currency),
								dividendYield: p.dividend_yield,
								dividendGrowth: p.dividend_growth_5y || effectiveSettings.expected_dividend_growth,
								drip: p.drip_enabled,
								taxWithholdingRate: p.tax_withholding_rate,
								buyFee: portfolio.drip_incurs_fee ? portfolio.buy_fee : undefined,
								buyFeeFlat: portfolio.drip_incurs_fee ? portfolio.buy_fee_flat : undefined,
							})),
					)
					// Include cash as a zero-yield asset so it counts toward portfolio
					// value and receives capital appreciation
					const assets: AssetProjection[] = totalCash > 0
						? [...positionAssets, { value: totalCash, dividendYield: 0, dividendGrowth: 0 }]
						: positionAssets

					return calculateProjection({
						assets,
						contribution: convert(effectiveSettings.monthly_contribution ?? 0, settingsCurrency),
						frequency: "Monthly",
						drip: effectiveSettings.drip_enabled ?? false,
						priceAppreciation: effectiveSettings.price_appreciation,
						dividendGrowthCap: effectiveSettings.dividend_growth_cap,
						incomeGoal: convert(effectiveSettings.monthly_expense_target ?? 0, settingsCurrency),
						years: 50,
						contributionSchedule,
						flattenAfterYear: 5,
					})
				})()
			: null

	// Build assets for scenario chart (same as projection, including cash)
	const scenarioAssets: AssetProjection[] | undefined =
		dashboard && effectiveSettings
			? (() => {
					const positions: AssetProjection[] = dashboard.portfolios.flatMap((portfolio) =>
						portfolio.positions
							.filter((p) => p.shares > 0)
							.map((p) => ({
								value: convert(p.price * p.shares, p.currency),
								dividendYield: p.dividend_yield,
								dividendGrowth: p.dividend_growth_5y || effectiveSettings.expected_dividend_growth,
								drip: p.drip_enabled,
								taxWithholdingRate: p.tax_withholding_rate,
								buyFee: portfolio.drip_incurs_fee ? portfolio.buy_fee : undefined,
								buyFeeFlat: portfolio.drip_incurs_fee ? portfolio.buy_fee_flat : undefined,
							})),
					)
					return totalCash > 0
						? [...positions, { value: totalCash, dividendYield: 0, dividendGrowth: 0 }]
						: positions
				})()
			: undefined

	// Use the post-flatten rate from the projection (year 5 income-weighted average)
	// rather than the year-0 average, since that's what the projection actually uses
	const weightedBaseDivGrowth = projection?.flattenedGrowthRate ?? null

	if (loading) {
		return <LoadingSkeleton />
	}

	if (error) {
		return (
			<div className="max-w-6xl mx-auto p-4">
				<p className="text-destructive">{error}</p>
			</div>
		)
	}

	if (!dashboard) return null

	const hasPortfolios = dashboard.portfolios.length > 0
	const annualGoal = convert(
		(effectiveSettings?.monthly_expense_target ?? dashboard.settings.monthly_expense_target) * 12,
		settingsCurrency,
	)

	const allPositions = dashboard.portfolios.flatMap((p) => p.positions)
	const annualDividendIncome = allPositions.reduce(
		(sum, pos) => sum + convert(pos.annual_dividend * pos.shares, pos.currency),
		0,
	)
	const totalPortfolioValue = allPositions.reduce(
		(sum, pos) => sum + convert(pos.price * pos.shares, pos.currency),
		0,
	)

	const capitalStart = totalPortfolioValue + totalCash

	const progressPct = annualGoal > 0 ? Math.round((annualDividendIncome / annualGoal) * 100) : 0
	const yearsToFreedom = projection?.freedomYear ? projection.freedomYear - new Date().getFullYear() : null
	const positionCount = dashboard.current_metrics.position_count

	return (
		<div className="max-w-6xl mx-auto p-4 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold flex items-center gap-2">
						<TrendingUp className="h-6 w-6 text-sky-500" />
						FIRE Dashboard
					</h1>
					<p className="text-muted-foreground text-sm mt-1">
						Track your path to financial independence through dividend income
					</p>
				</div>
				{hasPortfolios && (
					<Button variant="outline" size="sm" onClick={() => setShowShareDialog(true)} className="gap-1.5">
						<Share2 className="h-4 w-4" /> Share
					</Button>
				)}
			</div>

			{!hasPortfolios ? (
				<div className="text-center py-16 space-y-4">
					<p className="text-muted-foreground text-lg">
						Add your first portfolio to start tracking your path to financial freedom
					</p>
					<Link
						to="/dashboard"
						className="inline-flex items-center px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-md text-sm font-medium"
					>
						Go to Dashboard →
					</Link>
				</div>
			) : (
				<>
					{/* Hero metrics */}
					<FireHero
						annualDividendIncome={annualDividendIncome}
						settings={effectiveSettings!}
						freedomYear={projection?.freedomYear ?? null}
						portfolioValue={capitalStart}
					/>

					{/* Progress bar */}
					{annualGoal > 0 && (
						<FireProgressBar
							annualIncome={annualDividendIncome}
							settings={effectiveSettings!}
							milestoneYears={[]}
						/>
					)}

					{/* Streak */}
					<FireStreakBadge streak={dashboard.streak} />

					{/* Settings */}
					<FireSettingsPanel
						settings={dashboard.settings}
						onSave={handleSaveSettings}
						onSettingsChange={setSettingsPreview}
						saving={saving}
						portfolioValue={capitalStart}
						baseDivGrowth={weightedBaseDivGrowth}
					/>

					{/* Projection chart */}
					{projection && (
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
									30-Year Income Projection
									<InfoTip content="Projects annual dividend income based on current holdings, contributions, growth, and appreciation. Toggle 'Scenarios' to compare assumptions." />
								</p>
								<button
									onClick={() => setShowScenarios((s) => !s)}
									className={cn(
										"px-3 py-1 rounded-full text-xs font-medium border transition-all",
										showScenarios
											? "bg-sky-500 text-white border-transparent"
											: "text-muted-foreground border-border hover:border-sky-400",
									)}
								>
									Scenarios
								</button>
							</div>
							<div className="bg-white/60 dark:bg-black/20 backdrop-blur-md border border-sky-200/50 dark:border-sky-800/30 rounded-lg p-4">
								{showScenarios && scenarioAssets && effectiveSettings ? (
									<FireScenarioChart
										assets={scenarioAssets}
										settings={effectiveSettings}
										contribution={convert(
											effectiveSettings.monthly_contribution ?? 0,
											settingsCurrency,
										)}
										contributionSchedule={contributionSchedule}
										annualGoal={annualGoal}
										freedomYear={projection.freedomYear}
									/>
								) : (
									<FireProjectionChart
										data={projection.data}
										freedomYear={projection.freedomYear}
										annualGoal={annualGoal}
									/>
								)}
							</div>
						</div>
					)}

					{/* Milestones */}
					{projection && annualGoal > 0 && (
						<FireMilestones
							data={projection.data}
							annualGoal={annualGoal}
							currentIncome={annualDividendIncome}
						/>
					)}

					{/* Income history */}
					{dashboard.income_history.length > 0 && (
						<div className="bg-white/60 dark:bg-black/20 backdrop-blur-md border border-sky-200/50 dark:border-sky-800/30 rounded-lg p-4">
							<FireIncomeHistory history={dashboard.income_history} />
						</div>
					)}
				</>
			)}

			{/* Share dialog */}
			<FireShareDialog
				open={showShareDialog}
				onOpenChange={setShowShareDialog}
				progressPct={progressPct}
				freedomYear={projection?.freedomYear ?? null}
				yearsToFreedom={yearsToFreedom}
				positionCount={positionCount}
			/>
		</div>
	)
}
